import ArcoMessage from '@arco-design/web-vue/es/message/index.js'
import { isMobileViewport } from './theme.js'
import { Toast } from './toast.js'

let baseMessageSeed = 0
const desktopRecords = new Map()

const TYPE_ALIASES = {
  info: 'info',
  normal: 'normal',
  toast: 'info',
  success: 'success',
  error: 'error',
  warn: 'warning',
  warning: 'warning',
  loading: 'loading',
}

function isClientEnvironment() {
  return typeof window !== 'undefined' && typeof document !== 'undefined'
}

function getViewportWidth() {
  const widths = [
    window.innerWidth,
    document.documentElement?.clientWidth,
    window.visualViewport?.width,
  ]
    .map((width) => Math.round(Number(width) || 0))
    .filter((width) => width > 0)

  return widths.length ? Math.max(...widths) : Number.POSITIVE_INFINITY
}

/**
 * @description 根据视口宽度决定轻消息的呈现端：移动/平板使用 Toast，PC 使用 Arco Message。
 * @param {number} width - 当前视口宽度
 * @returns {'mobile'|'desktop'} 反馈呈现端
 */
export function resolveBaseMessageMode(width) {
  return isMobileViewport(width) ? 'mobile' : 'desktop'
}

function normalizeType(type) {
  return TYPE_ALIASES[type] || 'info'
}

function normalizeInput(input) {
  return typeof input === 'object' && input !== null ? input : { content: input }
}

function createNoopHandle() {
  const handle = {
    id: null,
    close: () => {},
    update: () => handle,
  }
  return handle
}

function createDesktopHandle(id) {
  return {
    id,
    close: () => {
      desktopRecords.get(id)?.close?.()
    },
    update: (input) => {
      const record = desktopRecords.get(id)
      const nextInput = normalizeInput(input)
      return openDesktopMessage({ ...nextInput, id }, nextInput.type || record?.type || 'info')
    },
  }
}

function openDesktopMessage(input, fallbackType) {
  const incoming = normalizeInput(input)
  const id = incoming.id || `base-message-${Date.now()}-${++baseMessageSeed}`
  const previous = desktopRecords.get(id)
  const type = normalizeType(incoming.type || fallbackType || previous?.type)
  const merged = { ...(previous?.config || {}), ...incoming, id, type }
  const onClose = merged.onClose
  const position = merged.position === 'bottom' || merged.direction === 'bottom' ? 'bottom' : 'top'
  const {
    direction: _direction,
    layout: _layout,
    disableBodyTouch: _disableBodyTouch,
    className,
    ...desktopConfig
  } = merged

  const result = ArcoMessage[type]({
    ...desktopConfig,
    class: desktopConfig.class || className,
    position,
    onClose: (closedId) => {
      desktopRecords.delete(id)
      onClose?.(closedId)
    },
  })

  desktopRecords.set(id, {
    close: result?.close,
    config: merged,
    type,
  })
  return createDesktopHandle(id)
}

function openBaseMessage(input, fallbackType = 'info') {
  if (!isClientEnvironment()) return createNoopHandle()

  const type = normalizeType(normalizeInput(input).type || fallbackType)
  if (resolveBaseMessageMode(getViewportWidth()) === 'mobile') {
    return Toast[type](input)
  }

  return openDesktopMessage(input, type)
}

function closeBaseMessage(id) {
  if (!isClientEnvironment()) return

  Toast.close(id)
  desktopRecords.get(id)?.close?.()
}

function clearBaseMessage() {
  if (!isClientEnvironment()) return

  let mobileError = null
  try {
    Toast.clear()
  } catch (error) {
    mobileError = error
  } finally {
    ArcoMessage.clear()
    desktopRecords.clear()
  }

  if (mobileError) throw mobileError
}

/**
 * @description 全站轻消息统一入口；业务层不感知 PC Message 与移动端 Toast 的实现差异。
 */
export const BaseMessage = {
  open: (config) => openBaseMessage(config, normalizeInput(config).type || 'info'),
  normal: (config) => openBaseMessage(config, 'normal'),
  info: (config) => openBaseMessage(config, 'info'),
  success: (config) => openBaseMessage(config, 'success'),
  error: (config) => openBaseMessage(config, 'error'),
  warn: (config) => openBaseMessage(config, 'warning'),
  warning: (config) => openBaseMessage(config, 'warning'),
  loading: (config) => openBaseMessage(config, 'loading'),
  close: closeBaseMessage,
  clear: clearBaseMessage,
  closeAll: clearBaseMessage,
}
